import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Play, Pencil, Trash2, AlertTriangle } from "lucide-react";
import { ago, DELIVERY_TEXT } from "@/lib/mongoMcp";
import MongoServerEditor from "@/components/mongo/servers/MongoServerEditor";
import MongoServerTestResult, { TestBadge } from "@/components/mongo/servers/MongoServerTestResult";
import ConfirmAction from "@/components/mongo/ConfirmAction";

const HEALTH_DOT = { online: "bg-emerald-400", stale: "bg-amber-400", offline: "bg-red-500", never: "bg-white/30", revoked: "bg-red-500" };
const ICON_BTN = "px-2.5 py-1.5 text-[11px] rounded-md border border-white/10 hover:bg-white/5 inline-flex items-center gap-1.5 transition-colors disabled:opacity-40";

export default function MongoServerCard({ row, environments, linkedBy, onChanged }) {
    const [editing, setEditing] = useState(false);
    const [testing, setTesting] = useState(false);
    const [showTest, setShowTest] = useState(false);
    const id = row.id;
    const test = async () => {
        setTesting(true);
        try {
            const { data } = await api.post(`/admin/mongo-mcp/servers/${id}/test`);
            toast[data.ok ? "success" : "error"](`${row.label}: ${data.ok ? "full check passed" : "full check failed"}`);
            setShowTest(true);
            onChanged();
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
        finally { setTesting(false); }
    };
    const remove = async () => {
        try { await api.delete(`/admin/mongo-mcp/servers/${id}`); toast.success(`${row.label} removed`); onChanged(); }
        catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };
    return (
        <div className="rounded-md border border-white/10 bg-[#0a0a0a] p-4 transition-colors hover:border-white/20" data-testid={`mongo-server-${id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                    <span className="font-display text-lg tracking-tight" data-testid={`mongo-server-label-${id}`}>{row.label}</span>
                    <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border border-white/15 text-white/55">{row.region} · {row.channel}</span>
                    <TestBadge test={row.last_test} id={id} onClick={() => setShowTest((v) => !v)} />
                </div>
                <div className="flex items-center gap-1.5">
                    <button onClick={test} disabled={testing || !row.url || !row.token_set} className={`${ICON_BTN} border-emerald-300/30 text-emerald-200 hover:bg-emerald-300/10`}
                        data-testid={`mongo-server-test-${id}`}><Play className="w-3 h-3" /> {testing ? "Testing…" : "Test"}</button>
                    <button onClick={() => setEditing((v) => !v)} className={ICON_BTN} data-testid={`mongo-server-edit-${id}`}><Pencil className="w-3 h-3" /> Edit</button>
                    <ConfirmAction title={`Remove ${row.label}?`} confirmLabel="Remove" onConfirm={remove} testid={`mongo-server-delete-${id}`} className={`${ICON_BTN} text-white/50`}
                        description={row.env_id ? "Its environment loses this connection and its MongoDB tools switch off within about 30 seconds." : "This connection is not linked to any environment."}>
                        <Trash2 className="w-3 h-3" />
                    </ConfirmAction>
                </div>
            </div>
            <ServerFacts row={row} />
            {row.warnings.length > 0 && (
                <ul className="mt-2.5 space-y-1" data-testid={`mongo-server-warnings-${id}`}>
                    {row.warnings.map((w) => <li key={w} className="text-[11px] text-amber-200/90 flex items-start gap-1.5"><AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />{w}</li>)}
                </ul>
            )}
            {editing && <MongoServerEditor row={row} environments={environments} linkedBy={linkedBy} onCancel={() => setEditing(false)}
                onSaved={() => { setEditing(false); onChanged(); }} />}
            {showTest && row.last_test && <MongoServerTestResult test={row.last_test} id={id} />}
        </div>
    );
}

function ServerFacts({ row }) {
    const env = row.env;
    const d = row.delivery || {};
    const stale = d.state === "received" && !d.field_map_current;
    const delivery = d.state === "received"
        ? `${DELIVERY_TEXT.received} ${ago(d.at)} · field map v${d.field_map_version ?? 0}${stale ? " — newer version not picked up yet" : ""}` : DELIVERY_TEXT[d.state];
    return (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-2 mt-3 text-[11px]">
            <Fact k="Radar environment" testid={`mongo-server-env-${row.id}`}>
                {env ? <span className="inline-flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full ${HEALTH_DOT[env.health] || "bg-white/30"}`} />{env.name}</span>
                    : <span className="text-white/35">Not linked</span>}
            </Fact>
            <Fact k="Server" testid={`mongo-server-url-${row.id}`}>{row.url_masked || <span className="text-white/35">—</span>}</Fact>
            <Fact k="Access token" testid={`mongo-server-token-${row.id}`}>{row.token_set ? row.token_mask : <span className="text-white/35">not set</span>}</Fact>
            <Fact k="Delivery" testid={`mongo-server-delivery-${row.id}`}>
                <span className={d.state === "waiting" || stale ? "text-amber-200" : d.state === "none" ? "text-white/35" : "text-emerald-200"}>{delivery}</span>
            </Fact>
        </div>
    );
}

function Fact({ k, testid, children }) {
    return (
        <div data-testid={testid} className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-0.5">{k}</div>
            <div className="font-mono text-xs text-white/85 truncate">{children}</div>
        </div>
    );
}
